import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Swords, Sparkles, Plus, Trash2, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { SectionCard } from "@/components/Brand/editors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useJobPoller } from "@/lib/useJobPoller";
import Markdown from "@/components/Markdown";

/** Iter 173 · Competitor tracker — expanded with 20+ fields + per-row AI. */

const THREAT_TONE = {
  low: "bg-emerald-50 text-emerald-700 border-emerald-300",
  medium: "bg-sky-50 text-sky-700 border-sky-300",
  high: "bg-amber-50 text-amber-700 border-amber-300",
  critical: "bg-rose-50 text-rose-700 border-rose-300",
};

const PRICING_OPTIONS = ["", "free", "freemium", "subscription", "one_time",
                            "enterprise", "usage_based"];
const FUNDING_OPTIONS = ["", "bootstrapped", "seed", "series_a", "series_b",
                            "series_c_plus", "public", "acquired"];

function toTextarea(v) {
  if (Array.isArray(v)) return v.join("\n");
  return v || "";
}
function fromTextarea(t) {
  return (t || "").split("\n").map((s) => s.trim()).filter(Boolean);
}

function RichField({ label, value, onChange, placeholder, rows = 2, testid }) {
  const hasMd = /\*\*|^-\s|^\d+\.\s|^#{1,4}\s/m.test(value || "");
  return (
    <div>
      <Label>{label}</Label>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)}
                  rows={rows} placeholder={placeholder}
                  className="w-full text-xs p-2 border border-border rounded-none font-mono leading-relaxed"
                  data-testid={testid} />
      {hasMd && value && (
        <div className="mt-1 border border-dashed border-border/60 p-2 bg-muted/20"
               data-testid={`${testid}-preview`}>
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Preview</div>
          <Markdown>{value}</Markdown>
        </div>
      )}
    </div>
  );
}

function CompetitorCard({ c, idx, wsId, brandId, expanded, onToggle, onChange,
                              onRemove, onAiAnalyzed, disableAi }) {
  const [busy, setBusy] = useState(false);
  const set = (k, v) => onChange({ ...c, [k]: v });

  const analyze = async () => {
    if (!c.name) { toast.error("Add a name first"); return; }
    setBusy(true);
    const t = toast.loading(`Analyzing ${c.name}…`);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${brandId}/competitors/${idx}/ai-analyze`);
      onAiAnalyzed(idx, r.data.competitor);
      toast.success(`Analysis complete for ${c.name}`, { id: t });
    } catch (e) {
      toast.error(apiErrorMessage(e, "AI analyze failed"), { id: t });
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card" data-testid={`competitor-card-${idx}`}>
      <div className="flex items-center gap-2 p-2 border-b border-border">
        <button type="button" onClick={onToggle}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`competitor-toggle-${idx}`}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <Input value={c.name || ""} onChange={(e) => set("name", e.target.value)}
                 placeholder="Competitor name"
                 className="h-8 text-sm font-semibold flex-1 rounded-none"
                 data-testid={`competitor-name-${idx}`} />
        <Input value={c.website || ""} onChange={(e) => set("website", e.target.value)}
                 placeholder="https://…"
                 className="h-8 text-xs w-48 rounded-none"
                 data-testid={`competitor-website-${idx}`} />
        <select value={c.threat_level || "medium"}
                  onChange={(e) => set("threat_level", e.target.value)}
                  className={`h-8 text-[10px] uppercase tracking-widest px-2 border rounded-none font-semibold ${THREAT_TONE[c.threat_level || "medium"]}`}
                  data-testid={`competitor-threat-${idx}`}>
          {["low", "medium", "high", "critical"].map((t) =>
            <option key={t} value={t}>{t}</option>)}
        </select>
        {c.ai_analyzed_at && (
          <span className="text-[10px] text-violet-700 font-mono" title="AI analyzed at"
                  data-testid={`competitor-ai-badge-${idx}`}>
            AI · {c.ai_analyzed_at.slice(0, 10)}
          </span>
        )}
        <Button size="sm" variant="outline"
                  className="h-8 rounded-none border-violet-500/50 text-violet-700 hover:bg-violet-50"
                  disabled={busy || disableAi}
                  onClick={analyze} data-testid={`competitor-analyze-btn-${idx}`}>
          <Sparkles className="w-3.5 h-3.5 mr-1" />
          {busy ? "…" : "AI"}
        </Button>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-none text-rose-600"
                  onClick={onRemove} data-testid={`competitor-remove-${idx}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {expanded && (
        <div className="p-3 space-y-3 text-xs">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Tagline</Label>
              <Input value={c.tagline || ""} onChange={(e) => set("tagline", e.target.value)}
                       className="h-8 text-xs rounded-none" data-testid={`competitor-tagline-${idx}`} />
            </div>
            <div className="col-span-2">
              <RichField label="Positioning" value={c.positioning}
                            onChange={(v) => set("positioning", v)}
                            testid={`competitor-positioning-${idx}`} rows={2} />
            </div>
          </div>

          <RichField label="Elevator pitch" value={c.elevator_pitch}
                        onChange={(v) => set("elevator_pitch", v)}
                        testid={`competitor-pitch-${idx}`} rows={2} />

          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label>Pricing model</Label>
              <select value={c.pricing_model || ""} onChange={(e) => set("pricing_model", e.target.value)}
                        className="h-8 w-full text-xs px-1 border border-border rounded-none bg-background"
                        data-testid={`competitor-pricing-model-${idx}`}>
                {PRICING_OPTIONS.map((v) =>
                  <option key={v} value={v}>{v || "—"}</option>)}
              </select>
            </div>
            <div>
              <Label>Pricing range</Label>
              <Input value={c.pricing_range || ""} onChange={(e) => set("pricing_range", e.target.value)}
                       className="h-8 text-xs rounded-none"
                       placeholder="₹5k-15k/mo"
                       data-testid={`competitor-pricing-range-${idx}`} />
            </div>
            <div>
              <Label>Target segment</Label>
              <Input value={c.target_segment || ""} onChange={(e) => set("target_segment", e.target.value)}
                       className="h-8 text-xs rounded-none"
                       data-testid={`competitor-segment-${idx}`} />
            </div>
            <div>
              <Label>Market share</Label>
              <Input value={c.market_share || ""} onChange={(e) => set("market_share", e.target.value)}
                       className="h-8 text-xs rounded-none"
                       placeholder="Top 3 in India"
                       data-testid={`competitor-market-share-${idx}`} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Strengths (one per line, ≤5)</Label>
              <textarea value={toTextarea(c.strengths)}
                          onChange={(e) => set("strengths", fromTextarea(e.target.value))}
                          rows={3} className="w-full text-xs p-2 border border-border rounded-none"
                          data-testid={`competitor-strengths-${idx}`} />
            </div>
            <div>
              <Label>Weaknesses</Label>
              <textarea value={toTextarea(c.weaknesses)}
                          onChange={(e) => set("weaknesses", fromTextarea(e.target.value))}
                          rows={3} className="w-full text-xs p-2 border border-border rounded-none"
                          data-testid={`competitor-weaknesses-${idx}`} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Unique features (they have, we don&apos;t)</Label>
              <textarea value={toTextarea(c.unique_features)}
                          onChange={(e) => set("unique_features", fromTextarea(e.target.value))}
                          rows={3} className="w-full text-xs p-2 border border-border rounded-none"
                          data-testid={`competitor-unique-${idx}`} />
            </div>
            <div>
              <Label>Common features (overlap with us)</Label>
              <textarea value={toTextarea(c.common_features)}
                          onChange={(e) => set("common_features", fromTextarea(e.target.value))}
                          rows={3} className="w-full text-xs p-2 border border-border rounded-none"
                          data-testid={`competitor-common-${idx}`} />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label>Funding stage</Label>
              <select value={c.funding_stage || ""} onChange={(e) => set("funding_stage", e.target.value)}
                        className="h-8 w-full text-xs px-1 border border-border rounded-none bg-background"
                        data-testid={`competitor-funding-${idx}`}>
                {FUNDING_OPTIONS.map((v) =>
                  <option key={v} value={v}>{v || "—"}</option>)}
              </select>
            </div>
            <div>
              <Label>Founded</Label>
              <Input type="number" value={c.founded_year || ""}
                       onChange={(e) => set("founded_year", parseInt(e.target.value) || null)}
                       className="h-8 text-xs rounded-none"
                       placeholder="2018"
                       data-testid={`competitor-founded-${idx}`} />
            </div>
            <div>
              <Label>Headcount</Label>
              <Input value={c.headcount || ""} onChange={(e) => set("headcount", e.target.value)}
                       className="h-8 text-xs rounded-none"
                       placeholder="50-200"
                       data-testid={`competitor-headcount-${idx}`} />
            </div>
            <div>
              <Label>HQ location</Label>
              <Input value={c.hq_location || ""} onChange={(e) => set("hq_location", e.target.value)}
                       className="h-8 text-xs rounded-none"
                       placeholder="Bengaluru"
                       data-testid={`competitor-hq-${idx}`} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Key customers</Label>
              <textarea value={toTextarea(c.key_customers)}
                          onChange={(e) => set("key_customers", fromTextarea(e.target.value))}
                          rows={2} className="w-full text-xs p-2 border border-border rounded-none"
                          data-testid={`competitor-customers-${idx}`} />
            </div>
            <div>
              <Label>Social handles</Label>
              <div className="text-[10px] text-muted-foreground mb-1">
                {(c.social_handles || []).length === 0 ? "—" : (
                  (c.social_handles || []).map((s, i) => (
                    <a key={i} href={s.url} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-0.5 mr-2 text-primary hover:underline">
                      {s.platform}<ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  ))
                )}
              </div>
            </div>
          </div>

          {(c.recent_news?.length > 0) && (
            <div>
              <Label>Recent news</Label>
              <ul className="text-xs mt-1 space-y-1" data-testid={`competitor-news-${idx}`}>
                {c.recent_news.map((n, i) => (
                  <li key={i}>
                    <span className="text-muted-foreground">{n.date || ""}</span>{" "}
                    {n.url ? <a href={n.url} target="_blank" rel="noreferrer"
                                    className="text-primary hover:underline">{n.title}</a>
                            : n.title}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="border border-emerald-300 bg-emerald-50 p-2 rounded-none">
              <Label className="!text-emerald-700">Our advantage</Label>
              <textarea value={c.our_advantage || ""}
                          onChange={(e) => set("our_advantage", e.target.value)}
                          rows={2} className="w-full text-xs p-2 border border-emerald-300 bg-white rounded-none font-mono leading-relaxed"
                          data-testid={`competitor-our-advantage-${idx}`} />
              {c.our_advantage && /\*\*|^-\s/m.test(c.our_advantage) && (
                <div className="mt-1 border-t border-emerald-300 pt-1"
                       data-testid={`competitor-our-advantage-${idx}-preview`}>
                  <Markdown>{c.our_advantage}</Markdown>
                </div>
              )}
            </div>
            <div className="border border-rose-300 bg-rose-50 p-2 rounded-none">
              <Label className="!text-rose-700">Their advantage</Label>
              <textarea value={c.their_advantage || ""}
                          onChange={(e) => set("their_advantage", e.target.value)}
                          rows={2} className="w-full text-xs p-2 border border-rose-300 bg-white rounded-none font-mono leading-relaxed"
                          data-testid={`competitor-their-advantage-${idx}`} />
              {c.their_advantage && /\*\*|^-\s/m.test(c.their_advantage) && (
                <div className="mt-1 border-t border-rose-300 pt-1"
                       data-testid={`competitor-their-advantage-${idx}-preview`}>
                  <Markdown>{c.their_advantage}</Markdown>
                </div>
              )}
            </div>
          </div>

          <RichField label="Notes" value={c.notes}
                        onChange={(v) => set("notes", v)}
                        testid={`competitor-notes-${idx}`} rows={2} />
        </div>
      )}
    </div>
  );
}

function Label({ children, className }) {
  return <label className={`text-[10px] uppercase tracking-widest text-muted-foreground font-semibold ${className || ""}`}>{children}</label>;
}

export default function BrandCompetitorsTab({ brand, onSaved }) {
  const wsId = brand.workspace_id;
  const [competitors, setCompetitors] = useState(brand.competitors || []);
  const [expanded, setExpanded] = useState(new Set([0]));
  const [busy, setBusy] = useState(false);
  const [bulkJobId, setBulkJobId] = useState(null);

  const { job: bulkJob } = useJobPoller(wsId, bulkJobId);
  useEffect(() => {
    if (bulkJob && (bulkJob.status === "done" || bulkJob.status === "partial")) {
      toast.success(`Analysed ${bulkJob.done} competitor${bulkJob.done === 1 ? "" : "s"}` + (bulkJob.failed ? ` · ${bulkJob.failed} failed` : ""));
      // Refresh brand data
      api.get(`/brands/${brand.id}`).then((r) => {
        setCompetitors(r.data.competitors || []);
        onSaved?.();
      });
      setBulkJobId(null);
    } else if (bulkJob && bulkJob.status === "failed") {
      toast.error("Bulk competitor analysis failed");
      setBulkJobId(null);
    }
  }, [bulkJob, brand.id, onSaved]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/brands/${brand.id}`, { competitors });
      toast.success("Saved");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const addRow = () => {
    setCompetitors((cs) => [...cs, { name: "", threat_level: "medium" }]);
    setExpanded((s) => new Set([...s, competitors.length]));
  };
  const removeRow = (idx) => {
    setCompetitors((cs) => cs.filter((_, i) => i !== idx));
    setExpanded((s) => {
      const nx = new Set();
      s.forEach((i) => { if (i < idx) nx.add(i); else if (i > idx) nx.add(i - 1); });
      return nx;
    });
  };
  const updateRow = (idx, val) => setCompetitors((cs) =>
    cs.map((c, i) => i === idx ? val : c));
  const toggleExpanded = (idx) => setExpanded((s) => {
    const nx = new Set(s);
    if (nx.has(idx)) nx.delete(idx); else nx.add(idx);
    return nx;
  });

  const startBulk = async () => {
    if (competitors.length === 0) { toast.error("Add at least one competitor first"); return; }
    // Save first so backend has the freshest names/websites
    await save();
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${brand.id}/competitors/ai-analyze-all`,
        { only_missing: true });
      setBulkJobId(r.data.job_id);
      toast.info(`Analysing ${r.data.total} competitor${r.data.total === 1 ? "" : "s"} in the background…`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not start bulk analyze"));
    }
  };

  return (
    <div data-testid="brand-competitors-tab">
      <SectionCard title="Competitor tracker" icon={Swords} onSave={save} busy={busy}
                       testid="brand-competitors-section"
                       hint={`${competitors.length} competitor${competitors.length === 1 ? "" : "s"} · positioning, pricing intel, threat level`}>
        <div className="flex items-center gap-2 mb-3">
          <Button size="sm" variant="outline" className="rounded-none" onClick={addRow}
                    data-testid="competitors-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add competitor
          </Button>
          <Button size="sm" variant="outline"
                    className="rounded-none border-violet-500/50 text-violet-700 hover:bg-violet-50"
                    onClick={startBulk} disabled={!!bulkJobId || competitors.length === 0}
                    data-testid="competitors-bulk-ai-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            {bulkJobId ? `Analysing… (${bulkJob?.done || 0}/${bulkJob?.total || 0})`
                          : "AI analyze all (missing)"}
          </Button>
        </div>

        <div className="space-y-2">
          {competitors.length === 0 ? (
            <div className="text-xs text-muted-foreground italic p-8 text-center border border-dashed border-border">
              No competitors yet. Add one manually or enrich existing ones with AI.
            </div>
          ) : competitors.map((c, idx) => (
            <CompetitorCard key={idx} c={c} idx={idx} wsId={wsId} brandId={brand.id}
                                expanded={expanded.has(idx)}
                                onToggle={() => toggleExpanded(idx)}
                                onChange={(v) => updateRow(idx, v)}
                                onRemove={() => removeRow(idx)}
                                onAiAnalyzed={(i, v) => updateRow(i, v)}
                                disableAi={!!bulkJobId} />
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
